import { useState, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import AddIcon from '@mui/icons-material/Add'
import CircularProgress from '@mui/material/CircularProgress'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { formatPrice } from '@/lib/formatPrice'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import { PATHS } from '@/router/paths'
import { useLocationRef } from '@/contexts/RefContext'
import { useAppDispatch, useAppSelector } from '@/store'
import {
	addItem,
	calculate,
	selectSelectedOrderType,
	selectDineInTableName,
} from '@/store/ticketSlice'
import { getMenuItemDetail } from '@/shared/api/menu'
import type { MenuItem as MenuItemData } from '@/shared/api/menu'
import {
	AddButton,
	CardBody,
	CardDescription,
	CardImage,
	CardImagePlaceholder,
	CardImageWrap,
	CardInfo,
	CardName,
	CardPrice,
	CardPriceRow,
	CardRoot,
	LoadingOverlay,
	LoadingText,
	LowStockLabel,
	SoldOutLabel,
} from './MenuItem.styled'

interface MenuItemProps {
	item: MenuItemData
	currency?: string
	isReOrder?: boolean
	pastOrderItemId?: string
}

export default function MenuItem({
	item,
	currency = 'USD',
	isReOrder = false,
	pastOrderItemId,
}: MenuItemProps) {
	const { t } = useFormatMessage()
	const intl = useIntl()
	const navigate = useNavigate()
	const locationRef = useLocationRef()
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const selectedOrderType = useAppSelector(selectSelectedOrderType)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const isDineIn = !!dineInTableName

	const isOutOfStock = item.stockStatus === 'OUT_OF_STOCK'
	const isLowStock = item.stockStatus === 'LOW_STOCK'
	const allowSpecialRequest = selectedOrderType?.allowSpecialRequest ?? false

	// useRef provides a synchronous guard — two rapid clicks in the same render frame
	// would both read isAdding state as false before the re-render, so React state alone
	// is not sufficient. The ref is set synchronously on the first click.
	const isAddingRef = useRef(false)
	const [isAdding, setIsAdding] = useState(false)

	const rawPrice = item.onlinePrice ?? item.price
	const price = rawPrice > 0 ? formatPrice(rawPrice, intl.locale, currency) : null

	// Preserve dineIn URL params on item page navigation so useLocationBootstrap keeps the
	// same TanStack Query key (['location', ref, true]) and does not re-fetch with isDineIn=false,
	// which would return a location without DINE_IN in orderTypes and clear selectedOrderTypeGuid.
	const dineInSearch = useMemo(
		() =>
			selectedOrderType?.ref === 'DINE_IN'
				? new URLSearchParams(
						dineInTableName
							? { dineIn: 'true', tableName: dineInTableName }
							: { dineIn: 'true' },
					).toString()
				: '',
		[selectedOrderType?.ref, dineInTableName],
	)

	async function handleItemClick(e: React.SyntheticEvent) {
		e.stopPropagation()
		if (isAddingRef.current || isOutOfStock) return

		// Reorder always navigates to the add-item page with pastOrderItemId so
		// AddItemPage can display the "Previously Ordered" section (STO-3268).
		if (isReOrder && pastOrderItemId) {
			const reorderParams = new URLSearchParams(dineInSearch)
			reorderParams.set('pastOrderItemId', pastOrderItemId)
			void navigate(
				{
					pathname: PATHS.addItem(locationRef, item.ref),
					search: `?${reorderParams.toString()}`,
				},
				{ preventScrollReset: true },
			)
			return
		}

		isAddingRef.current = true
		setIsAdding(true)
		try {
			const detail = await queryClient.fetchQuery({
				queryKey: ['menu-item-detail', locationRef, item.ref, isDineIn],
				queryFn: () => getMenuItemDetail(locationRef ?? '', item.ref, isDineIn),
				staleTime: QUERY_STALE_TIMES.REALTIME,
			})
			if (detail.modifierSets.length === 0 && !allowSpecialRequest) {
				dispatch(
					addItem({
						locationRef: locationRef ?? '',
						itemRef: detail.ref,
						quantity: 1,
						specialRequest: null,
						modifiers: [],
					}),
				)
				void dispatch(calculate())
			} else {
				void navigate(
					dineInSearch
						? `${PATHS.addItem(locationRef, item.ref)}?${dineInSearch}`
						: PATHS.addItem(locationRef, item.ref),
					{ preventScrollReset: true },
				)
			}
		} catch {
			void navigate(
				dineInSearch
					? `${PATHS.addItem(locationRef, item.ref)}?${dineInSearch}`
					: PATHS.addItem(locationRef, item.ref),
				{ preventScrollReset: true },
			)
		} finally {
			isAddingRef.current = false
			setIsAdding(false)
		}
	}

	const addButton = (
		<AddButton
			isOutOfStock={isOutOfStock}
			isReOrder={isReOrder}
			aria-label={t('component:menuItem:addItem:label', { name: item.name })}
			aria-hidden={isOutOfStock || undefined}
			tabIndex={-1}
			disabled={isAdding}
			onClick={(e) => void handleItemClick(e)}
		>
			<AddIcon />
		</AddButton>
	)

	const imageSection = (
		<CardImageWrap isReOrder={isReOrder}>
			{item.imageUrl800x800 ? (
				<CardImage
					isReOrder={isReOrder}
					isOutOfStock={isOutOfStock}
					src={item.imageUrl800x800}
					alt=""
					loading="lazy"
					width={144}
					height={144}
				/>
			) : (
				<CardImagePlaceholder aria-hidden="true" />
			)}
			{!isReOrder && addButton}
		</CardImageWrap>
	)

	return (
		<>
			<CardRoot
				isOutOfStock={isOutOfStock}
				isReOrder={isReOrder}
				aria-disabled={isOutOfStock || undefined}
				data-testid="menu-item"
				role={isOutOfStock ? undefined : 'button'}
				tabIndex={isOutOfStock ? -1 : 0}
				onClick={isOutOfStock ? undefined : (e) => void handleItemClick(e)}
				onKeyDown={
					isOutOfStock
						? undefined
						: (e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									void handleItemClick(e)
								}
							}
				}
			>
				{isReOrder && imageSection}
				<CardBody>
					<CardInfo>
						<CardName isOutOfStock={isOutOfStock}>{item.name}</CardName>
						{item.description?.trim() &&
							item.description.trim() !== item.name.trim() && (
								<CardDescription isOutOfStock={isOutOfStock} isReOrder={isReOrder}>
									{item.description}
								</CardDescription>
							)}
					</CardInfo>

					<CardPriceRow>
						{price !== null && (
							<CardPrice isOutOfStock={isOutOfStock}>{price}</CardPrice>
						)}
						{isOutOfStock && (
							<SoldOutLabel>{t('component:menuItem:soldOut:label')}</SoldOutLabel>
						)}
						{isLowStock && (
							<LowStockLabel>{t('component:menuItem:lowStock:badge')}</LowStockLabel>
						)}
					</CardPriceRow>
				</CardBody>
				{!isReOrder && imageSection}
				{isReOrder && addButton}
			</CardRoot>

			{isAdding &&
				createPortal(
					<LoadingOverlay role="status" aria-label={t('component:itemModal:loading')}>
						<CircularProgress size={48} sx={{ color: 'text.primary' }} />
						<LoadingText>{t('component:itemModal:loading')}</LoadingText>
					</LoadingOverlay>,
					document.body,
				)}
		</>
	)
}
